// Rebindable controls (§13.2/§13.3), persisted to localStorage so they survive reload (T-CLI-004).
//
// D137 (§7.2) — controls v2. The action set is the user's finalized taxonomy: kick verbs are UNIVERSAL
// ("because all those actions must be possible also when a ball is in-flight"), the defensive verbs are
// no-possession, and three keys legitimately carry two meanings each. The split is resolved in
// `contextMapper.ts`, never in the sim.

import { BTN } from '@fafi/shared';

export type ActionBinding =
  // universal
  | 'shoot'
  | 'passDirect'
  | 'passLong'
  | 'loft'
  | 'sprint'
  | 'diveFlop'
  // no possession
  | 'tackle' // the SAFE take — never fouls (the TRAP machinery, relabeled)
  | 'tackleHard' // the committal challenge with a foul probability (TACKLE_STAND)
  | 'slide'
  | 'block'
  | 'call'
  | 'shield'
  | 'shove' // D137/PT4-P14: kept in the sim (bots use it) but ships UNBOUND for humans
  | 'punch'
  // keeper
  | 'gkDiveL'
  | 'gkDiveR'
  // movement + meta (D127 — these used to be hard-coded outside the map; AZERTY could not play)
  | 'moveUp'
  | 'moveDown'
  | 'moveLeft'
  | 'moveRight'
  | 'switchPlayer'
  | 'cheatSheet';

export type Bindings = Record<ActionBinding, string>;

/**
 * D137 (§7.2) — the layout. Three keys are deliberately shared, because the context decides:
 *   Space  = pass direct · call for it (a teammate has the ball)
 *   E      = loft modifier · hard tackle (they have the ball) · keeper dive right (defending a penalty)
 *   Ctrl   = block stance (not possessing) · shield (possessing)
 * `CONFLICT_GROUP` below is what lets the controls screen accept those without "swapping" them apart.
 * R and V are freed (through → RMB, loft → E); G is reserved for future actions.
 */
export const DEFAULT_BINDINGS: Bindings = {
  shoot: 'Mouse0', // hold = aim (the precision goal-aim engages here since D137), release = fire
  passDirect: 'Space',
  passLong: 'Mouse2', // the D112 lane-aware long verb IS the long pass — relabeled, same bit
  loft: 'KeyE',
  sprint: 'ShiftLeft',
  diveFlop: 'KeyX', // D137: BTN.DIVE has existed since M5 and was never in DEFAULT_BINDINGS
  tackle: 'KeyF',
  tackleHard: 'KeyE',
  slide: 'KeyC',
  block: 'ControlLeft',
  call: 'Space',
  shield: 'ControlLeft',
  shove: '', // PT4-P14: the user's action list omits the barge — unbound, still rebindable
  punch: 'KeyT',
  gkDiveL: 'KeyQ',
  gkDiveR: 'KeyE',
  moveUp: 'KeyW',
  moveDown: 'KeyS',
  moveLeft: 'KeyA',
  moveRight: 'KeyD',
  switchPlayer: 'Tab',
  cheatSheet: 'KeyH',
};

/** D127 — actions that may be left unbound. Everything else must always have a key. */
export const OPTIONAL_BINDINGS: ReadonlySet<ActionBinding> = new Set<ActionBinding>(['gkDiveL', 'gkDiveR', 'shove']);

/**
 * D137 — actions that may legally share a key, because a context tells them apart. Same group ⇒ a
 * conflict is not a conflict, and the controls screen must NOT swap them. Different groups ⇒ the D127
 * swap still applies.
 */
export const CONFLICT_GROUP: Readonly<Partial<Record<ActionBinding, string>>> = {
  passDirect: 'ball-vs-mate',
  call: 'ball-vs-mate',
  loft: 'ball-vs-opponent',
  tackleHard: 'ball-vs-opponent',
  gkDiveR: 'ball-vs-opponent', // E again — the keeper-penalty context wins over both
  block: 'possessing',
  shield: 'possessing',
};

/** Which sim button bit each rebindable action drives (§4.2). Move + camera are handled separately. */
export const BINDING_BTN: Readonly<Record<ActionBinding, number>> = {
  shoot: BTN.SHOOT,
  passDirect: BTN.PASS,
  passLong: BTN.THROUGH, // D137: THROUGH retires as a LABEL, not as a verb — nothing on the wire changes
  loft: BTN.LOFT,
  sprint: BTN.SPRINT,
  diveFlop: BTN.DIVE,
  tackle: BTN.TRAP, // "never produces a foul, spill or stumble" — which is exactly the safe take (PT4-P13)
  tackleHard: BTN.TACKLE, // the committal standing challenge, full referee pipeline
  slide: BTN.SLIDE,
  block: BTN.CONTAIN,
  call: BTN.CALL | BTN.SHOUT, // D45 Item 8: one key both calls and shouts (two existing bits)
  shield: BTN.SHIELD,
  shove: BTN.SHOVE,
  punch: BTN.PUNCH,
  gkDiveL: BTN.GK_DIVE_L,
  gkDiveR: BTN.GK_DIVE_R,
  // D127: movement, switching and the cheat sheet are not sim buttons — the input layer and the main
  // loop handle them — so they carry no bit.
  moveUp: 0,
  moveDown: 0,
  moveLeft: 0,
  moveRight: 0,
  switchPlayer: 0,
  cheatSheet: 0,
};

/** The actions the context mapper resolves into a button word (everything with a bit). */
export const CONTEXT_ACTIONS: readonly ActionBinding[] = (Object.keys(BINDING_BTN) as ActionBinding[]).filter((a) => BINDING_BTN[a] !== 0);

const LEGACY_KEY = 'fafi.bindings';
/** §14.2's spec'd home. `storage.ts` has defined `fafi.settings.bindings` since M2 and nothing read it. */
const SETTINGS_KEY = 'fafi.settings';
/** D137 — bumped when the LAYOUT changes meaning, not merely when an action is added. */
export const BINDINGS_VERSION = 2;

function readSettings(storage: Pick<Storage, 'getItem'>): Record<string, unknown> {
  try {
    const raw = storage.getItem(SETTINGS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Keep only ids this build knows, and only string values — an adversarial or newer blob cannot inject. */
function sanitize(blob: unknown): Partial<Bindings> {
  const out: Partial<Bindings> = {};
  if (blob === null || typeof blob !== 'object') return out;
  const rec = blob as Record<string, unknown>;
  for (const id of Object.keys(DEFAULT_BINDINGS) as ActionBinding[]) {
    const v = Object.prototype.hasOwnProperty.call(rec, id) ? rec[id] : undefined;
    if (typeof v === 'string') out[id] = v;
  }
  return out;
}

/**
 * D127 — bindings live in `fafi.settings.bindings` (§14.2); a one-time migration lifted the legacy
 * `fafi.bindings` key. Anything unparseable falls back to defaults rather than throwing, so a corrupt
 * value can never stop the game booting.
 *
 * D137 — a stored set from BEFORE controls v2 is DISCARDED rather than merged, and the reason is
 * concrete: v1's `contain: Mouse2` is a known id whose meaning moved (RMB became the long pass, the
 * stance moved to Ctrl), so a mechanical merge would leave RMB driving both — the audit's exact
 * shadowing case. Every kick verb moved keys in the same wave, so there is no honest per-id migration to
 * write; the version marker is the discriminator, and a v2 blob merges normally.
 */
export function loadBindings(storage: Pick<Storage, 'getItem'>): Bindings {
  try {
    const settings = readSettings(storage);
    const stored = settings['bindings'];
    if (stored !== undefined && stored !== null && typeof stored === 'object') {
      const version = typeof settings['bindingsVersion'] === 'number' ? (settings['bindingsVersion'] as number) : 1;
      if (version < BINDINGS_VERSION) return { ...DEFAULT_BINDINGS };
      return { ...DEFAULT_BINDINGS, ...sanitize(stored) };
    }
    if (!storage.getItem(LEGACY_KEY)) return { ...DEFAULT_BINDINGS };
    return { ...DEFAULT_BINDINGS }; // the legacy key predates v2 by definition
  } catch {
    return { ...DEFAULT_BINDINGS };
  }
}

export function saveBindings(storage: Pick<Storage, 'setItem' | 'getItem' | 'removeItem'>, b: Bindings): void {
  const settings = readSettings(storage);
  settings['bindings'] = { ...DEFAULT_BINDINGS, ...sanitize(b) };
  settings['bindingsVersion'] = BINDINGS_VERSION;
  storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  storage.removeItem?.(LEGACY_KEY); // migrated — the old key never gets read again
}
